import { Box, Button, Card, CardContent, Chip, IconButton, TextField } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useBlockedAccessGuard } from "../hooks/useBlockedAccessGuard";
import { financeApi } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { nextTagPaletteColor } from "../lib/tagColors";
import type { Group, Tag, TagPayload } from "../lib/types";

function TagColorSquare({ color, ariaLabel, onChange }: { color: string; ariaLabel: string; onChange: (color: string) => void }) {
  return (
    <label className="tag-color-square" title="Color" style={{ backgroundColor: color }}>
      <input
        className="tag-color-square-input"
        type="color"
        value={color}
        aria-label={ariaLabel}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export function TagsPage({ group, onBlockedAccess }: { group: Group; onBlockedAccess?: () => void }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("");
  const [name, setName] = useState("");
  const [color, setColor] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [confirmDeleteTag, setConfirmDeleteTag] = useState<Tag | null>(null);
  const [draggedTagId, setDraggedTagId] = useState<string | null>(null);
  const [dragOverTagId, setDragOverTagId] = useState<string | null>(null);

  const tagsQuery = useQuery({ queryKey: queryKeys.tags(group.id), queryFn: () => financeApi.tags(group.id) });
  const tags = tagsQuery.data || [];
  const activeTags = tags.filter((tag) => !tag.is_archived);
  const deletedTags = tags.filter((tag) => tag.is_archived);
  const newTagColor = color || nextTagPaletteColor(activeTags.length);

  useBlockedAccessGuard([tagsQuery.error], onBlockedAccess);

  const invalidateTags = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.tags(group.id) }),
      queryClient.invalidateQueries({ queryKey: ["popular-tags"] }),
      queryClient.invalidateQueries({ queryKey: queryKeys.records }),
    ]);
  };

  const createMutation = useMutation({
    mutationFn: (payload: TagPayload) => financeApi.createTag(group.id, payload),
    onSuccess: async () => {
      setName("");
      setColor("");
      setStatus("");
      await invalidateTags();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: TagPayload }) => financeApi.updateTag(group.id, id, payload),
    onSuccess: async () => {
      setEditingId(null);
      setStatus("");
      await invalidateTags();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => financeApi.deleteTag(group.id, id),
    onSuccess: async () => {
      setConfirmDeleteTag(null);
      setStatus("");
      await invalidateTags();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => financeApi.restoreTag(group.id, id),
    onSuccess: async () => {
      setStatus("");
      await invalidateTags();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const reorderMutation = useMutation({
    mutationFn: (tagIds: string[]) => financeApi.reorderTags(group.id, tagIds),
    onSuccess: (ordered) => {
      setStatus("");
      queryClient.setQueryData(queryKeys.tags(group.id), ordered);
    },
    onError: async (error) => {
      setStatus(error instanceof Error ? error.message : "Request failed");
      await queryClient.invalidateQueries({ queryKey: queryKeys.tags(group.id) });
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setStatus("Enter a tag name.");
      return;
    }
    createMutation.mutate({ name: trimmed, color: newTagColor });
  };

  const startEditing = (tag: Tag) => {
    setEditingId(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color);
  };

  const saveEditing = (tag: Tag) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      setStatus("Enter a tag name.");
      return;
    }
    updateMutation.mutate({ id: tag.id, payload: { name: trimmed, color: editColor } });
  };

  const dropOnTag = (targetId: string) => {
    if (!draggedTagId || draggedTagId === targetId) return;
    const orderedIds = activeTags.map((tag) => tag.id);
    const fromIndex = orderedIds.indexOf(draggedTagId);
    const toIndex = orderedIds.indexOf(targetId);
    if (fromIndex < 0 || toIndex < 0) return;
    orderedIds.splice(toIndex, 0, ...orderedIds.splice(fromIndex, 1));
    const reordered = orderedIds
      .map((id) => activeTags.find((tag) => tag.id === id))
      .filter((tag): tag is Tag => Boolean(tag));
    queryClient.setQueryData(queryKeys.tags(group.id), [...reordered, ...deletedTags]);
    reorderMutation.mutate(orderedIds);
  };

  const renderRow = (tag: Tag) => {
    const isEditing = editingId === tag.id;
    const isArchived = tag.is_archived;
    const isDragTarget = dragOverTagId === tag.id && draggedTagId !== tag.id;
    return (
      <div
        key={tag.id}
        className={`tags-settings-row${isArchived ? " archived" : ""}${isDragTarget ? " drag-target" : ""}`}
        onDragOver={(event) => {
          if (!draggedTagId || isArchived) return;
          event.preventDefault();
          setDragOverTagId(tag.id);
        }}
        onDrop={(event) => {
          event.preventDefault();
          dropOnTag(tag.id);
          setDraggedTagId(null);
          setDragOverTagId(null);
        }}
      >
        {!isArchived ? (
          <span
            className="tags-settings-drag-handle"
            draggable={!isEditing}
            aria-label={`Reorder ${tag.name}`}
            title="Drag to reorder"
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = "move";
              setDraggedTagId(tag.id);
            }}
            onDragEnd={() => {
              setDraggedTagId(null);
              setDragOverTagId(null);
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </span>
        ) : (
          <span className="tags-settings-drag-handle placeholder" aria-hidden="true" />
        )}
        {isEditing ? (
          <Box className="tags-settings-edit">
            <TagColorSquare color={editColor} ariaLabel={`Choose color for ${tag.name}`} onChange={setEditColor} />
            <TextField
              size="small"
              value={editName}
              autoFocus
              slotProps={{ htmlInput: { maxLength: 60 } }}
              onChange={(event) => setEditName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  saveEditing(tag);
                }
                if (event.key === "Escape") setEditingId(null);
              }}
            />
          </Box>
        ) : (
          <Chip className="chip tags-settings-chip" size="small" label={tag.name} style={{ backgroundColor: tag.color, color: "#fff", opacity: isArchived ? 0.55 : 1 }} />
        )}
        <span className="tags-settings-actions">
          {isEditing ? (
            <>
              <IconButton className="records-icon-button" type="button" aria-label={`Save ${tag.name}`} title="Save" disabled={updateMutation.isPending} onClick={() => saveEditing(tag)}>
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton className="records-icon-button" type="button" aria-label="Cancel editing" title="Cancel" onClick={() => setEditingId(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          ) : (
            <>
              {!isArchived ? (
                <IconButton className="records-icon-button" type="button" aria-label={`Edit ${tag.name}`} title="Edit" onClick={() => startEditing(tag)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton className="records-icon-button" type="button" aria-label={`Restore ${tag.name}`} title="Restore" disabled={restoreMutation.isPending} onClick={() => restoreMutation.mutate(tag.id)}>
                  <RestoreOutlinedIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                className="records-icon-button"
                type="button"
                aria-label={isArchived ? `Delete ${tag.name} forever` : `Delete ${tag.name}`}
                title={isArchived ? "Delete forever" : "Delete"}
                disabled={deleteMutation.isPending}
                onClick={() => (isArchived ? setConfirmDeleteTag(tag) : deleteMutation.mutate(tag.id))}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </span>
      </div>
    );
  };

  return (
    <>
      <header className="topbar">
        <Box>
          <h1>Tags</h1>
          <p>Tags add a second dimension to records — the channel or context, independent of category.</p>
        </Box>
      </header>

      <section className="content">
        <Card className="records-panel">
          <CardContent className="records-panel-content">
            <form className="tags-settings-form" onSubmit={submit}>
              <TagColorSquare
                color={newTagColor}
                ariaLabel="Choose color for new tag"
                onChange={setColor}
              />
              <TextField
                label="Tag name"
                size="small"
                value={name}
                placeholder="Wolt"
                slotProps={{ htmlInput: { maxLength: 60 } }}
                onChange={(event) => setName(event.target.value)}
              />
              <Button variant="contained" size="small" type="submit" disabled={createMutation.isPending}>
                Add
              </Button>
            </form>
            {status ? <p className="status error">{status}</p> : null}
            {tagsQuery.isLoading ? <p className="status">Loading tags...</p> : null}
            <div className="tags-settings-list">
              {!tagsQuery.isLoading && tags.length === 0 ? <p className="users-group-empty">No tags yet. Add the first one above.</p> : null}
              {activeTags.map(renderRow)}
              {deletedTags.length ? <p className="tags-settings-section-label">Deleted</p> : null}
              {deletedTags.map(renderRow)}
            </div>
          </CardContent>
        </Card>
      </section>
      {confirmDeleteTag ? (
        <ConfirmDialog
          titleId="deleteTagForeverTitle"
          title="Delete tag forever?"
          description={`"${confirmDeleteTag.name}" will be removed from all records permanently.`}
          confirmLabel="Delete forever"
          closeAriaLabel="Close delete tag dialog"
          pending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(confirmDeleteTag.id)}
          onClose={() => setConfirmDeleteTag(null)}
        />
      ) : null}
    </>
  );
}
